// hooks/use-sidebar.ts
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from '../../../Redux/store';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from '../../../apis/auth/logout';
import { useUnreviewedCount } from '../../../apis/utils/unreviewed-count';
import { useGetHelpDeskUnreadCount } from '../../../apis/help-desks/get-help-desk-unread-count';
import { setHelpDeskCount, toggleSidebar } from '../../../Redux/slices/ui';
import socket from '../../../lib/socket-io';
import { logout } from '../../../Redux/slices/auth';


const useSidebar = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const pathName = window.location.pathname;
    const isDesktop = window.innerWidth > 850;

    const isSidebarCollapsed = useAppSelector((state) => state.ui.isSidebarCollapsed);
    const helpDeskCount = useAppSelector((state) => state.ui.helpDeskUnreadCount);

    const { mutateAsync: signOut } = useSignOut();
    const { data: unreviewedData } = useUnreviewedCount();
    const { data: helpDeskData } = useGetHelpDeskUnreadCount();

    const unReviewedDriverCount = unreviewedData?.count ?? 0;

    const [sectionsOpen, setSectionsOpen] = useState({
        main: pathName === "/dashboard" || pathName === "/dashboard/analytics",
        usersAccounts: ["/dashboard/users", "/dashboard/business", "/dashboard/drivers"].includes(pathName),
        operations: ["/dashboard/orders", "/dashboard/batches", "/dashboard/driver-documentation-update-requests", "/dashboard/feedback", "/dashboard/announcements"].includes(pathName),
        support: ["/dashboard/help-desk", "/dashboard/states"].includes(pathName),
        management: ["/dashboard/admins", "/dashboard/batch-configs", "/dashboard/promo-codes", "/dashboard/subscription-plans", "/dashboard/offers", "/dashboard/accountSettings"].includes(pathName),
    });

    useEffect(() => {
        if (helpDeskData?.count !== undefined) {
            dispatch(setHelpDeskCount(helpDeskData.count));
        }
    }, [helpDeskData]);

    useEffect(() => {
        const handleNewHelpDeskCount = (data: {
            data: { totalUnreadCount: number };
            type: string;
        }) => {
            if (data.type === "all_complaints_unread_count") {
                dispatch(setHelpDeskCount(data.data.totalUnreadCount));
            }
        };
        socket.on("all_complaints_unread_count", handleNewHelpDeskCount);
        return () => {
            socket.off("all_complaints_unread_count", handleNewHelpDeskCount);
        };
    }, []);

    const handleLogout = async () => {
        await signOut();
        dispatch(logout());
        navigate("/login", { replace: true });
    };

    const handleSidebarToggle = () => {
        if (!isDesktop) dispatch(toggleSidebar());
    };

    const toggleSection = (section: keyof typeof sectionsOpen) => {
        setSectionsOpen({
            main: false,
            usersAccounts: false,
            operations: false,
            support: false,
            management: false,
            [section]: !sectionsOpen[section],
        });
    };

    return {
        sectionsOpen,
        toggleSection,
        isSidebarCollapsed,
        isDesktop,
        helpDeskCount,
        unReviewedDriverCount,
        handleLogout,
        handleSidebarToggle,
    };
};

export default useSidebar;